import { trackAuth, AUTH_EVENT_TYPE, IDP, UNSPECIFIED, AUTH_TYPE, AUTH_METHOD, TRIGGER_TYPE, TRIGGER_OBJECT, INPUT_SELECTOR } from '@zillow/auth-utils';
import { profileIntervalBegin, profileIntervalEnd, profileIntervalFail } from '@zillow/client-profiler';
import { dataLayerPush, REGISTRATION_EVENT } from '@zillow/data-layer-push';
import { track, identifyUser } from '@zillow/universal-analytics';
import request from 'superagent';

var SDK_SRC = 'https://accounts.google.com/gsi/client';
var GOOGLE_ACCOUNTS_URI = 'https://accounts.google.com';
var ID_TOKEN_ENDPOINT = '/user/account/services/ExternalAuthentication.htm';
function NOOP() {}
var TRACK_LOAD = 'authopen/onetap/';
var TRACK_LOGIN = 'signin/onetap/';
var TRACK_REGISTER = 'register/onetap/';
var TRACK_DISMISS = 'dismiss/onetap/';
var TRACK_SERVICE_FAILURE = 'serviceFailure';
var BLACKLIST_DOMAIN = '@zillowgroup.com';
var PROFILE_EVENT = 'reglogin:google_one_tap';
var BLACKLIST_ERROR_FN = function BLACKLIST_ERROR_FN(email) {
  return "Email " + email + " is not authorized for One Tap sign in, please use email/password credentials";
};

/**
 * A util function to gets the trigger location prop for clickstream events.
 * Locations to be determined `main_connect_landing` and `building_details`.
 *
 * @param {string} path URL path
 * @returns {string} trigger_location_nm (`authentication_form` as default value)
 */

var getTriggerLocationByURL = function getTriggerLocationByURL(path) {
  if (path.startsWith('/user/acct') || path.startsWith('/user/account')) {
    return 'standalone_authentication';
  }

  if (path === '/') {
    return 'home';
  }

  if (path.startsWith('/homes')) {
    return 'search_results';
  }

  if (path.startsWith('/homedetails')) {
    return 'home_details';
  }

  return 'authentication_form';
};

/* eslint-disable no-unused-expressions */
var ENDPOINT_FAILURE_MSG = 'Unknown Google error. Please try again.';
var SMARTLOCK_SELECTOR = 'iframe[src^="https://smartlock.google.com"]';
var _smartlock;
var PING_INTERVAL = 1000;
var PING_TIME_LIMIT = 7000;
var _cancel = false;
var cancelPing = false;
/**
 * Get a visible element
 * @param {String} selector
 */

function getVisibleElement(selector) {
  var element = document.querySelector(selector);

  if (element && element.style.display !== 'none') {
    return element;
  }

  return null;
}
/**
 * Wait for an element to be present unless canceled or timed out
 * @param {Number|Date} startTime
 * @param {Function} elementFn
 * @param {Function} callback
 * @returns {Function} Function to cancel/uncancel ping
 */
// eslint-disable-next-line no-unused-vars

function ping(startTime, elementFn, callback) {
  if (elementFn === void 0) {
    elementFn = function elementFn(_) {};
  }

  if (callback === void 0) {
    callback = function callback(_) {};
  }

  setTimeout(function () {
    var element = elementFn();

    if (element) {
      callback(element);
    } else if (!cancelPing && Date.now() - startTime <= PING_TIME_LIMIT) {
      ping(startTime, elementFn, callback);
    }
  }, PING_INTERVAL);
}
/**
 * Global callback when sdk loads
 *
 * @param {Object} smartlock
 * @param {Object} smartlockConfig
 ** @param {String} smartlockConfig.clientId
 ** @param {String} smartlockConfig.context
 */

function __onSmartLockLoad(smartlock, smartlockConfig, handlers) {
  return new Promise(function ($return, $error) {
    var _smartlockConfig, clientId, context, _await$smartlock$hint, id, idToken, type;

    if (smartlockConfig === void 0) {
      smartlockConfig = {};
    }

    if (handlers === void 0) {
      handlers = {};
    }

    _smartlockConfig = smartlockConfig, clientId = _smartlockConfig.clientId, context = _smartlockConfig.context;
    _smartlock = smartlock;

    var $Try_1_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_1_Catch = function (err) {
      try {
        type = err.type;

        switch (type) {
          case 'userCanceled':
            newLaneTrack(TRACK_DISMISS);
            handlers.dismiss && handlers.dismiss();
            return $return();

          default:
            cancelPing = true;
        }

        return $Try_1_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      return Promise.resolve(smartlock.hint({
        supportedAuthMethods: [GOOGLE_ACCOUNTS_URI],
        supportedIdTokenProviders: [{
          uri: GOOGLE_ACCOUNTS_URI,
          clientId: clientId,
          context: context
        }]
      })).then(function ($await_3) {
        try {
          _await$smartlock$hint = $await_3, id = _await$smartlock$hint.id, idToken = _await$smartlock$hint.idToken;

          // to avoid race conditions we may need to cancel the init bit
          if (_cancel) {
            smartlock.cancelLastOperation().then(function () {// Closing...
            });
            return $return();
          }

          if (id && id.indexOf(BLACKLIST_DOMAIN) > -1) {
            handleBlackListed(id, handlers.failure);
          }

          if (idToken) {
            handleIdToken(idToken, handlers);
          }

          return $Try_1_Post();
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
}
/**
 * Convenience funciton trackEvent
 *
 * @param {String} action
 */

function newLaneTrack(action) {
  if (action === void 0) {
    action = TRACK_LOAD;
  }

  var gaProps = {
    action: action,
    category: 'auth',
    label: 'onetap//'
  };

  if (action === TRACK_LOGIN) {
    trackAuth(AUTH_EVENT_TYPE.LoginAttempt)({
      pageProps: {
        clickstream_trigger: {
          trigger_location_nm: 'google_one_tap|auth_form'
        },
        auth_form: {
          idp_cd: IDP.Zillow,
          entry_point_nm: UNSPECIFIED,
          auth_type_cd: AUTH_TYPE.AccountLogin
        }
      },
      componentProps: {
        clickstream_trigger: {
          trigger_source_nm: 'button_to_login'
        },
        auth_form: {
          auth_method_cd: AUTH_METHOD.GoogleOneTap
        }
      },
      gaProps: gaProps
    });
  } else if (action === TRACK_REGISTER) {
    trackAuth(AUTH_EVENT_TYPE.RegisterAttempt)({
      pageProps: {
        clickstream_trigger: {
          trigger_location_nm: 'google_one_tap|auth_form'
        },
        auth_form: {
          idp_cd: IDP.Zillow,
          entry_point_nm: UNSPECIFIED,
          auth_type_cd: AUTH_TYPE.AccountRegister
        }
      },
      componentProps: {
        clickstream_trigger: {
          trigger_source_nm: 'button_to_register'
        },
        auth_form: {
          auth_method_cd: AUTH_METHOD.GoogleOneTap
        }
      },
      gaProps: gaProps
    });
  } else {
    var _TRACK_DISMISS$TRACK_;

    // TODO:
    // No corresponding auth0 event implemented in auth-utils.
    // Once we figure out how google one tap works with auth0, we can implement in auth-utils and replace these track calls.
    // Note: that Google is migrating to its new FedCM (Federated Credential Management) API.
    track(gaProps, {
      newLaneEvent: (_TRACK_DISMISS$TRACK_ = {}, _TRACK_DISMISS$TRACK_[TRACK_DISMISS] = {
        envelope: {
          event_type_id: '3065',
          event_type_version_id: '3',
          event_template_id: '5',
          event_template_version_id: '1',
          event_client_start_dtm: new Date().toISOString()
        },
        clickstream_trigger: {
          trigger_type_nm: TRIGGER_TYPE.Interaction,
          trigger_location_nm: getTriggerLocationByURL(window.location.pathname),
          trigger_source_nm: 'click_to_dismiss',
          trigger_object_nm: TRIGGER_OBJECT.Default,
          input_selector_nm: INPUT_SELECTOR.Other
        },
        semantic: {
          semantic_event_nm: 'auth_form_close',
          topic_tag_txt: ['account_login', 'account_register']
        },
        auth_form: {
          auth_method_cd: AUTH_METHOD.GoogleOneTap
        }
      }, _TRACK_DISMISS$TRACK_[TRACK_LOAD] = {
        envelope: {
          event_type_id: '2944',
          event_type_version_id: '5',
          event_template_id: '5',
          event_template_version_id: '1',
          event_client_start_dtm: new Date().toISOString()
        },
        clickstream_trigger: {
          trigger_type_nm: TRIGGER_TYPE.Impression,
          trigger_location_nm: getTriggerLocationByURL(window.location.pathname),
          trigger_source_nm: 'one_tap_auth_open',
          trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
          input_selector_nm: INPUT_SELECTOR.Other
        },
        semantic: {
          semantic_event_nm: 'view_content',
          topic_tag_txt: ['account_login', 'account_register']
        },
        auth_form: {
          auth_method_cd: AUTH_METHOD.GoogleOneTap
        }
      }, _TRACK_DISMISS$TRACK_[TRACK_SERVICE_FAILURE] = {
        envelope: {
          event_type_id: '2944',
          event_type_version_id: '5',
          event_template_id: '5',
          event_template_version_id: '1',
          event_client_start_dtm: new Date().toISOString()
        },
        clickstream_trigger: {
          trigger_type_nm: TRIGGER_TYPE.Impression,
          trigger_location_nm: getTriggerLocationByURL(window.location.pathname),
          trigger_source_nm: 'one_tap_service_failure',
          trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
          input_selector_nm: INPUT_SELECTOR.Other
        },
        semantic: {
          semantic_event_nm: 'view_content',
          topic_tag_txt: ['account_login', 'account_register']
        },
        auth_form: {
          auth_method_cd: AUTH_METHOD.GoogleOneTap
        }
      }, _TRACK_DISMISS$TRACK_)[action]
    });
  }
}
/**
 * Get the proper GA event from a typical reg/login response
 * @param {Object} response
 * @return {String}
 */

function getGAEvent(response) {
  if (response === void 0) {
    response = {};
  }

  if (response.googleAnalyticsEvent && response.googleAnalyticsEvent.action && response.googleAnalyticsEvent.action.indexOf('register') > -1) {
    return TRACK_REGISTER;
  }

  return TRACK_LOGIN;
}
/**
 * Load the one tap sdk
 */

function loadSDK(cb) {
  if (cb === void 0) {
    cb = function cb() {};
  }

  (function (d, s, id) {
    // eslint-disable-line no-unexpected-multiline
    var element = d.getElementsByTagName(s)[0];
    var fjs = element;
    var js = d.createElement(s);
    js.id = id;
    js.src = SDK_SRC;
    js.onload = cb;
    fjs.parentNode.insertBefore(js, fjs);
  })(document, 'script', 'google-1-tap');
}
/**
 * Register / login with one tap token
 *
 * @param {String} idToken
 * @param {Object} handlers
 * @param {Function} handlers.prefetch
 * @param {Function} handlers.success
 * @param {Function} handlers.failure
 */

function handleIdToken(idToken, _ref) {
  var prefetch = _ref.prefetch,
      success = _ref.success,
      failure = _ref.failure;
  prefetch();
  request.post(ID_TOKEN_ENDPOINT).field('idToken', idToken).end(function (err, res) {
    if (err || !res.ok) {
      failure(ENDPOINT_FAILURE_MSG);
    } else {
      var loginResponse;

      try {
        loginResponse = JSON.parse(res.text);
      } catch (ex) {
        console.log('Error reading Google 1-tap login response', ex);
      }

      success(loginResponse);

      if (res && res.body) {
        identifyUser({
          userId: res.body.googleAnalyticsId
        });
      }
    }
  });
}
/**
 *
 * @param {String} email
 * @param {Function} errorHandler
 */

function handleBlackListed(email, errorHandler) {
  if (errorHandler === void 0) {
    errorHandler = NOOP;
  }

  errorHandler(BLACKLIST_ERROR_FN(email));
}
/**
 * Compose handlers for request
 *
 * @param {Function} success
 * @param {Function} failure
 * @returns {Object}
 */

function getOnLoadHandlers(_success, _failure) {
  return {
    prefetch: function prefetch() {
      profileIntervalBegin(PROFILE_EVENT);
    },
    success: function success(res) {
      var eventType = getGAEvent(res);
      profileIntervalEnd(PROFILE_EVENT);

      _success(res);

      newLaneTrack(eventType);

      if (eventType === TRACK_REGISTER) {
        dataLayerPush(REGISTRATION_EVENT);
      }
    },
    failure: function failure(msg) {
      profileIntervalFail(PROFILE_EVENT);

      _failure(msg);

      newLaneTrack(TRACK_SERVICE_FAILURE);
    }
  };
}

function handlePromptNotifications(PromptMomentNotification) {
  switch (PromptMomentNotification.getMomentType()) {
    case 'display':
      break;

    case 'skipped':
      break;

    case 'dismissed':
      newLaneTrack(TRACK_DISMISS);
      break;
  }
}

function isOneTapAvailable() {
  return Boolean(global.google && global.google.accounts && global.google.accounts.id);
}
/**
 * Initialize the thing
 *
 * @param {String} clientId
 * @param {String} context
 * @param {Object} handlers
 * @param {Function} handlers.successHandler
 * @param {Function} handlers.dismissHandler
 */


function init(clientId, context, _ref2) {
  if (clientId === void 0) {
    clientId = '';
  }

  if (context === void 0) {
    context = '';
  }

  var _ref2$successHandler = _ref2.successHandler,
      successHandler = _ref2$successHandler === void 0 ? NOOP : _ref2$successHandler,
      _ref2$dismissHandler = _ref2.dismissHandler,
      dismissHandler = _ref2$dismissHandler === void 0 ? NOOP : _ref2$dismissHandler;
  _cancel = false;

  if (!isOneTapAvailable()) {
    loadSDK(function () {
      global.google.accounts.id.initialize({
        client_id: clientId,
        context: context,
        callback: function callback(res) {
          console.log(res);
          handleIdToken(res.credential, getOnLoadHandlers(successHandler, dismissHandler));
        }
      });
      newLaneTrack(TRACK_LOAD);
      global.google.accounts.id.prompt(handlePromptNotifications);
    });
  } else {
    global.google.accounts.id.prompt(handlePromptNotifications);
  } // if (!window.onSmartLockLoad) {
  //     window.onSmartLockLoad = smartlock => {
  //         // No load event currently for smartlock window so need to ping for visible element
  //         cancelPing = false;
  //         ping(
  //             Date.now(),
  //             () => getVisibleElement(smartlockSelector),
  //             () => {
  //                 track(TRACK_LOAD);
  //             }
  //         );
  //         __onSmartLockLoad(smartlock, config, {
  //             ...getOnLoadHandlers(successHandler, errorHandler),
  //             dismiss: dismissHandler,
  //         });
  //     };
  // } else if (_smartlock) {
  //     window.onSmartLockLoad(_smartlock);
  // }

}
/**
 * Close smartlock dialog
 */

function close() {
  if (isOneTapAvailable()) {
    global.google.accounts.id.cancel();
  }
}

export { ENDPOINT_FAILURE_MSG, PING_INTERVAL, PING_TIME_LIMIT, SMARTLOCK_SELECTOR, __onSmartLockLoad, _smartlock, close, getGAEvent, getOnLoadHandlers, getVisibleElement, handleBlackListed, handleIdToken, init, loadSDK, newLaneTrack, ping };
